import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  Email,
  Storage,
  CalendarToday,
  CheckCircle,
  Lock,
  Visibility,
  VisibilityOff,
  ContentCopy,
  Info,
  AccountCircle,
  DnsOutlined,
  ForwardToInbox,
  MarkEmailRead,
  SendOutlined,
  Policy,
} from '@mui/icons-material';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';

interface AccountData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  storageQuota: number;
  storageUsed: number;
  domain?: {
    name: string;
  };
  createdAt: string;
  lastLoginAt: string;
}

interface UserSettings {
  enableForwarding: boolean;
  forwardingAddress: string;
  keepCopyOnForward: boolean;
}

// Server configuration derived from the platform
const MAIL_SERVER = 'mail.cruvzmail.com';

const SERVER_CONFIG = {
  imap: [
    { label: 'Incoming (SSL)', host: MAIL_SERVER, port: '993', mode: 'SSL / TLS' },
    { label: 'Incoming (STARTTLS)', host: MAIL_SERVER, port: '143', mode: 'STARTTLS' },
  ],
  pop: [
    { label: 'Incoming', host: MAIL_SERVER, port: '995', mode: 'SSL / TLS' },
  ],
  smtp: [
    { label: 'Outgoing (SSL)', host: MAIL_SERVER, port: '465', mode: 'SSL / TLS' },
    { label: 'Outgoing (STARTTLS)', host: MAIL_SERVER, port: '587', mode: 'STARTTLS' },
  ],
};

const safeNum = (val: any): number => {
  const n = Number(val);
  return isNaN(n) || !isFinite(n) ? 0 : n;
};

const formatBytes = (bytes: number): string => {
  const n = safeNum(bytes);
  if (n === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return parseFloat((n / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const CopyButton: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
      <IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5, color: copied ? 'success.main' : 'action.active' }}>
        <ContentCopy sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
};

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

const ServerConfigTable: React.FC<{ rows: { label: string; host: string; port: string; mode: string }[]; showAuth?: boolean; username?: string }> = ({ rows, showAuth, username }) => (
  <Box>
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: showAuth ? 2 : 0 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.5 }}></TableCell>
            <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Server / Host</TableCell>
            <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Port</TableCell>
            <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Mode</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 } }}>
              <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{row.label}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 500 }}>{row.host}</Typography>
                  <CopyButton value={row.host} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 600 }}>{row.port}</Typography>
                  <CopyButton value={row.port} />
                </Box>
              </TableCell>
              <TableCell>
                <Chip label={row.mode} size="small" color="success" variant="outlined" sx={{ fontSize: '0.78rem', fontWeight: 500 }} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    {showAuth && username && (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'info.50', borderColor: 'info.200' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Policy sx={{ fontSize: 16, color: 'info.main' }} /> Authentication
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Username:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{username}</Typography>
              <CopyButton value={username} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Password:</Typography>
            <Typography variant="body2">Your CruvzMail account password</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Auth method:</Typography>
            <Typography variant="body2">Normal Password (PLAIN / LOGIN)</Typography>
          </Box>
        </Box>
      </Paper>
    )}
  </Box>
);

const MailAccountsSettings: React.FC = () => {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [accountData, setAccountData] = useState<AccountData | null>(null);
  const [settings, setSettings] = useState<UserSettings>({ enableForwarding: false, forwardingAddress: '', keepCopyOnForward: true });
  const [savingForward, setSavingForward] = useState(false);
  const [popFetchBehavior, setPopFetchBehavior] = useState('keep_unread');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Password change state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [profileRes, settingsRes] = await Promise.all([
        api.get('/users/profile'),
        api.get('/users/settings').catch(() => ({ data: {} })),
      ]);
      const profile = profileRes.data?.data || profileRes.data;
      setAccountData(profile);
      const s = settingsRes.data?.data || settingsRes.data;
      if (s) {
        setSettings({
          enableForwarding: s.enableForwarding ?? false,
          forwardingAddress: s.forwardingAddress ?? '',
          keepCopyOnForward: s.keepCopyOnForward ?? true,
        });
      }
    } catch (error) {
      console.error('Failed to fetch account:', error);
      setSnackbar({ open: true, message: 'Failed to load account data', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      setSnackbar({ open: true, message: 'All password fields are required', severity: 'error' });
      return;
    }
    if (newPassword.length < 8) {
      setSnackbar({ open: true, message: 'New password must be at least 8 characters', severity: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setSnackbar({ open: true, message: 'New passwords do not match', severity: 'error' });
      return;
    }
    if (oldPassword === newPassword) {
      setSnackbar({ open: true, message: 'New password must be different from current password', severity: 'error' });
      return;
    }
    try {
      setChangingPassword(true);
      await api.put('/users/password', { oldPassword, newPassword });
      setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
      setOldPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to change password', severity: 'error' });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveForwarding = async () => {
    if (settings.enableForwarding && !settings.forwardingAddress) {
      setSnackbar({ open: true, message: 'Please enter a forwarding email address', severity: 'error' });
      return;
    }
    if (settings.enableForwarding && settings.forwardingAddress === (accountData?.email || user?.email)) {
      setSnackbar({ open: true, message: 'Forwarding address cannot be your own email', severity: 'error' });
      return;
    }
    try {
      setSavingForward(true);
      await api.put('/users/settings', {
        enableForwarding: settings.enableForwarding,
        forwardingAddress: settings.forwardingAddress,
        keepCopyOnForward: settings.keepCopyOnForward,
      });
      setSnackbar({ open: true, message: 'Forwarding settings saved', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to save forwarding settings', severity: 'error' });
    } finally {
      setSavingForward(false);
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const storageUsed = safeNum(accountData?.storageUsed);
  const storageQuota = safeNum(accountData?.storageQuota);
  const storagePercent = storageQuota > 0 ? Math.min((storageUsed / storageQuota) * 100, 100) : 0;

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}><CircularProgress size={32} /></Box>;
  }

  const displayName = accountData?.firstName && accountData?.lastName
    ? `${accountData.firstName} ${accountData.lastName}`
    : accountData?.email?.split('@')[0] || 'User';

  const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const emailAddress = accountData?.email || user?.email || '';

  const TABS = [
    { label: 'General', icon: <AccountCircle sx={{ fontSize: 18 }} /> },
    { label: 'POP', icon: <MarkEmailRead sx={{ fontSize: 18 }} /> },
    { label: 'IMAP', icon: <DnsOutlined sx={{ fontSize: 18 }} /> },
    { label: 'SMTP', icon: <SendOutlined sx={{ fontSize: 18 }} /> },
    { label: 'Forwards', icon: <ForwardToInbox sx={{ fontSize: 18 }} /> },
  ];

  return (
    <Box>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px' }}>{snackbar.message}</Alert>
      </Snackbar>

      <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5 }}>Mail Account</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Manage your mail account, client configuration, and forwarding settings</Typography>

      {/* Tab Navigation */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '0.875rem', minHeight: 48, gap: 0.75 } }}
          >
            {TABS.map((tab, i) => (
              <Tab key={i} label={tab.label} icon={tab.icon} iconPosition="start" />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* ── GENERAL ── */}
          <TabPanel value={activeTab} index={0}>
            {/* Profile Card */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: '1.75rem', fontWeight: 500 }}>{initials}</Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{displayName}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                    <Email sx={{ fontSize: 16 }} />{emailAddress}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
                    <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label={accountData?.isActive ? 'Active' : 'Inactive'} size="small" color={accountData?.isActive ? 'success' : 'default'} sx={{ fontWeight: 500 }} />
                    {accountData?.domain?.name && <Chip label={accountData.domain.name} size="small" variant="outlined" />}
                    <Chip label={accountData?.role || 'user'} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* Storage Usage */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Storage color="primary" /> Storage Usage
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">{formatBytes(storageUsed)} used</Typography>
                  <Typography variant="body2" color="text.secondary">{formatBytes(storageQuota)} total</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={storagePercent}
                  sx={{ height: 8, borderRadius: 4, bgcolor: 'divider', '& .MuiLinearProgress-bar': { bgcolor: storagePercent > 90 ? 'error.main' : storagePercent > 70 ? 'warning.main' : 'primary.main', borderRadius: 4 } }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">{(100 - storagePercent).toFixed(1)}% of your storage is available</Typography>
            </Paper>

            {/* Account Details */}
            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Account Details</Typography>
              <List disablePadding>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}><Email color="action" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Email Address" secondary={emailAddress} />
                </ListItem>
                <Divider />
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}><CalendarToday color="action" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Account Created" secondary={formatDate(accountData?.createdAt || '')} />
                </ListItem>
                <Divider />
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}><CalendarToday color="action" fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Last Login" secondary={formatDate(accountData?.lastLoginAt || '')} />
                </ListItem>
              </List>
            </Paper>

            {/* Change Password */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Lock color="primary" /> Change Password
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>Update your account password. You'll need your current password first.</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 400 }}>
                {[
                  { label: 'Current Password', value: oldPassword, set: setOldPassword, show: showOldPassword, toggle: () => setShowOldPassword(!showOldPassword) },
                  { label: 'New Password', value: newPassword, set: setNewPassword, show: showNewPassword, toggle: () => setShowNewPassword(!showNewPassword), helper: 'Minimum 8 characters' },
                  { label: 'Confirm New Password', value: confirmPassword, set: setConfirmPassword, show: showConfirmPassword, toggle: () => setShowConfirmPassword(!showConfirmPassword), error: confirmPassword.length > 0 && newPassword !== confirmPassword, helper: confirmPassword.length > 0 && newPassword !== confirmPassword ? 'Passwords do not match' : '' },
                ].map((field, i) => (
                  <TextField
                    key={i}
                    label={field.label}
                    type={field.show ? 'text' : 'password'}
                    value={field.value}
                    onChange={(e) => field.set(e.target.value)}
                    fullWidth size="small"
                    error={field.error}
                    helperText={field.helper || ''}
                    InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={field.toggle} edge="end">{field.show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> }}
                  />
                ))}
                <Box>
                  <Button variant="contained" onClick={handleChangePassword} disabled={changingPassword || !oldPassword || !newPassword || !confirmPassword} sx={{ textTransform: 'none', borderRadius: 1.5, px: 3 }}>
                    {changingPassword ? <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> : null}
                    {changingPassword ? 'Saving...' : 'Change Password'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </TabPanel>

          {/* ── POP ── */}
          <TabPanel value={activeTab} index={1}>
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'info.50', borderColor: 'info.200', display: 'flex', gap: 1.5 }}>
              <Info sx={{ color: 'info.main', fontSize: 20, mt: 0.2 }} />
              <Typography variant="body2" color="text.secondary">
                <strong>POP3 (Post Office Protocol)</strong> downloads emails from the server to your email client and, by default, removes them from the server. Use IMAP instead if you want to keep messages synced across multiple devices.
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>POP Fetch Behavior</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Choose what happens to emails in your inbox after they are fetched by a POP client.</Typography>
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>Behavior after POP fetch</InputLabel>
                <Select value={popFetchBehavior} label="Behavior after POP fetch" onChange={(e) => setPopFetchBehavior(e.target.value)}>
                  <MenuItem value="keep_unread">Keep emails unread in Inbox</MenuItem>
                  <MenuItem value="mark_read">Mark emails as read in Inbox</MenuItem>
                  <MenuItem value="move_archive">Move emails to Archive</MenuItem>
                  <MenuItem value="delete">Delete from Inbox</MenuItem>
                </Select>
              </FormControl>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>POP3 Server Configuration</Typography>
              <ServerConfigTable rows={SERVER_CONFIG.pop} showAuth username={emailAddress} />
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>POP3 Configuration (Quick Reference)</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 24px', alignItems: 'center' }}>
                {[
                  ['Server', MAIL_SERVER], ['Port', '995'], ['Encryption', 'SSL / TLS'],
                  ['Username', emailAddress], ['Password', 'Your account password'],
                ].map(([k, v]) => (
                  <React.Fragment key={k}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{k}:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: k === 'Server' || k === 'Port' ? 'monospace' : 'inherit' }}>{v}</Typography>
                  </React.Fragment>
                ))}
              </Box>
            </Paper>
          </TabPanel>

          {/* ── IMAP ── */}
          <TabPanel value={activeTab} index={2}>
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'info.50', borderColor: 'info.200', display: 'flex', gap: 1.5 }}>
              <Info sx={{ color: 'info.main', fontSize: 20, mt: 0.2 }} />
              <Typography variant="body2" color="text.secondary">
                <strong>IMAP (Internet Message Access Protocol)</strong> keeps your emails on the server and syncs them across all your devices in real time. Recommended for use on multiple devices (phone, tablet, desktop).
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>IMAP Server Configuration</Typography>
              <ServerConfigTable rows={SERVER_CONFIG.imap} showAuth username={emailAddress} />
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>IMAP Configuration (Quick Reference)</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 24px', alignItems: 'center' }}>
                {[
                  ['Server', MAIL_SERVER], ['Port (SSL)', '993'], ['Port (STARTTLS)', '143'],
                  ['Encryption', 'SSL / TLS  or  STARTTLS'],
                  ['Username', emailAddress], ['Password', 'Your account password'],
                ].map(([k, v]) => (
                  <React.Fragment key={k}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{k}:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: k === 'Server' || k.startsWith('Port') ? 'monospace' : 'inherit' }}>{v}</Typography>
                  </React.Fragment>
                ))}
              </Box>
            </Paper>
          </TabPanel>

          {/* ── SMTP ── */}
          <TabPanel value={activeTab} index={3}>
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'info.50', borderColor: 'info.200', display: 'flex', gap: 1.5 }}>
              <Info sx={{ color: 'info.main', fontSize: 20, mt: 0.2 }} />
              <Typography variant="body2" color="text.secondary">
                <strong>SMTP (Simple Mail Transfer Protocol)</strong> is used to send emails from your email client. Configure these details in your email client's outgoing mail server settings. Authentication is required.
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>SMTP Server Configuration</Typography>
              <ServerConfigTable rows={SERVER_CONFIG.smtp} showAuth username={emailAddress} />
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>SMTP Configuration (Quick Reference)</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 24px', alignItems: 'center' }}>
                {[
                  ['Server', MAIL_SERVER], ['Port (SSL)', '465'], ['Port (STARTTLS)', '587'],
                  ['Encryption', 'SSL / TLS  or  STARTTLS'],
                  ['Authentication', 'Required (Normal Password)'],
                  ['Username', emailAddress], ['Password', 'Your account password'],
                ].map(([k, v]) => (
                  <React.Fragment key={k}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{k}:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: k === 'Server' || k.startsWith('Port') ? 'monospace' : 'inherit' }}>{v}</Typography>
                  </React.Fragment>
                ))}
              </Box>
            </Paper>
          </TabPanel>

          {/* ── FORWARDS ── */}
          <TabPanel value={activeTab} index={4}>
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'info.50', borderColor: 'info.200', display: 'flex', gap: 1.5 }}>
              <Info sx={{ color: 'info.main', fontSize: 20, mt: 0.2 }} />
              <Typography variant="body2" color="text.secondary">
                <strong>Email Forwarding</strong> automatically forwards all incoming emails to another address. You can optionally keep a copy in your inbox.
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2.5 }}>Forwarding Settings</Typography>

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enableForwarding}
                      onChange={(e) => setSettings(s => ({ ...s, enableForwarding: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Enable email forwarding</Typography>
                      <Typography variant="caption" color="text.secondary">All incoming emails will be forwarded to the address below</Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                />
              </Box>

              <Box sx={{ opacity: settings.enableForwarding ? 1 : 0.4, transition: 'opacity 0.2s', pointerEvents: settings.enableForwarding ? 'auto' : 'none', display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 440 }}>
                <TextField
                  label="Forward to email address"
                  type="email"
                  value={settings.forwardingAddress}
                  onChange={(e) => setSettings(s => ({ ...s, forwardingAddress: e.target.value }))}
                  fullWidth
                  size="small"
                  placeholder="recipient@example.com"
                  helperText="Emails will be forwarded to this address"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.keepCopyOnForward}
                      onChange={(e) => setSettings(s => ({ ...s, keepCopyOnForward: e.target.checked }))}
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>Keep a copy in inbox</Typography>
                      <Typography variant="caption" color="text.secondary">Forwarded emails will also remain in your inbox</Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Button
                variant="contained"
                onClick={handleSaveForwarding}
                disabled={savingForward}
                sx={{ textTransform: 'none', borderRadius: 1.5, px: 3 }}
              >
                {savingForward ? <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> : null}
                {savingForward ? 'Saving...' : 'Save Forwarding Settings'}
              </Button>
            </Paper>
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default MailAccountsSettings;

